import { memo, useCallback, useMemo } from 'react';
import Skeleton from '../Skeleton.jsx';
import { areMenuItemPropsEqual } from './menuCompare.js';
import MenuItemLeading from './MenuItemLeading.jsx';
import {
  MENU_ITEM_CHECKBOX_SIZE,
  MENU_ITEM_CLOSE_ICON_SIZE,
  MENU_ITEM_CLOSE_PADDING,
  MENU_ITEM_CONTENT_GAP,
  MENU_ITEM_DESCRIPTION_CLASS,
  MENU_ITEM_FORCED_ACTIVE_SURFACE,
  MENU_ITEM_HOVER_SURFACE,
  MENU_ITEM_LABEL_CLASS,
  MENU_ITEM_LOADING_SKELETON_CLASS,
  MENU_ITEM_ROW_BASE,
  MENU_ITEM_SIZE_CLASS,
} from './menuTokens.js';
import {
  DEFAULT_MENU_HIGHLIGHT_ICON,
  DEFAULT_MENU_ICON,
  handleMenuItemCloseClick,
  handleMenuItemKeyDown,
  joinClasses,
  normalizeCheckbox,
  normalizeMenuItemSize,
  normalizeMenuItemState,
  resolveAriaSelected,
  resolveAvatar,
  resolveLucideIcon,
} from './menu.utils.js';
import { Check, X } from 'lucide-react';

/**
 * Single menu row — Figma Menu-items (4762:7152).
 *
 * @param {import('./menuTypes.js').MenuItemProps} props
 */
const MenuItem = ({
  label,
  description,
  size = 'medium',
  state = 'default',
  disabled = false,
  loading = false,
  selected = false,
  selectedSurface = true,
  role: roleProp,
  checkbox = false,
  icon = false,
  highlightIcon = false,
  avatar = false,
  subtextMenu = false,
  button = false,
  showClose,
  onClose,
  closeAriaLabel = 'Remove item',
  onClick,
  className = '',
}) => {
  const sz = normalizeMenuItemSize(size);
  const visualState = normalizeMenuItemState(state, disabled, loading);
  const isDisabled = visualState === 'disabled';
  const isLoading = visualState === 'skeleton';
  const checkboxConfig = normalizeCheckbox(checkbox);
  const isSelected = Boolean(selected);
  const isForcedActive = visualState === 'hover' || visualState === 'focus';
  const showActiveSurface =
    (isForcedActive || (selectedSurface && isSelected)) && !isDisabled && !isLoading;
  const isInteractive = Boolean(onClick) && !isDisabled && !isLoading;
  const hasCheckbox = Boolean(checkboxConfig);
  const rowClick = isInteractive ? onClick : undefined;
  // An explicit `role` wins over the checkbox-derived one.
  const resolvedRole = roleProp ?? (hasCheckbox ? 'menuitemcheckbox' : 'menuitem');
  const ariaSelected = resolveAriaSelected({ role: resolvedRole, hasCheckbox, isSelected });
  const textMuted = isDisabled ? 'text-content-muted' : 'text-content';
  const descMuted = isDisabled ? 'text-content-muted' : 'text-content-secondary';

  const resolvedIcon = resolveLucideIcon(icon, DEFAULT_MENU_ICON);
  const resolvedHighlightIcon = resolveLucideIcon(highlightIcon, DEFAULT_MENU_HIGHLIGHT_ICON);
  const avatarConfig = resolveAvatar(avatar);

  const showTrailingButton = button || showClose;
  const showSubtext = subtextMenu && (description || label);
  const subtext = showSubtext ? (description || label) : undefined;

  const rowClass = useMemo(
    () => joinClasses(
      MENU_ITEM_ROW_BASE,
      MENU_ITEM_SIZE_CLASS[sz],
      isInteractive && joinClasses('cursor-pointer', MENU_ITEM_HOVER_SURFACE),
      showActiveSurface && MENU_ITEM_FORCED_ACTIVE_SURFACE,
      isDisabled && 'cursor-not-allowed',
      isLoading && 'h-14 bg-surface px-4 py-2 dark:bg-surface',
      className,
    ),
    [sz, isInteractive, showActiveSurface, isDisabled, isLoading, className, hasCheckbox],
  );

  const handleKeyDown = useCallback(
    (event) => handleMenuItemKeyDown(onClick, event),
    [onClick],
  );

  const handleClose = useCallback(
    (event) => handleMenuItemCloseClick(onClose, event),
    [onClose],
  );

  return (
    <div
      role={resolvedRole}
      tabIndex={isInteractive ? 0 : undefined}
      aria-disabled={isDisabled || undefined}
      aria-checked={hasCheckbox ? isSelected : undefined}
      aria-selected={ariaSelected}
      aria-label={hasCheckbox ? checkboxConfig.ariaLabel ?? `Select ${label}` : undefined}
      onClick={rowClick}
      onKeyDown={isInteractive ? handleKeyDown : undefined}
      className={rowClass}
    >
      {checkboxConfig ? (
        <CheckboxIndicator
          size={MENU_ITEM_CHECKBOX_SIZE[sz]}
          checked={isSelected}
          disabled={isDisabled || isLoading}
        />
      ) : null}

      {isLoading ? (
        <Skeleton className={MENU_ITEM_LOADING_SKELETON_CLASS} animation />
      ) : (
        <span className={joinClasses('flex min-w-0 flex-1 items-center', MENU_ITEM_CONTENT_GAP[sz])}>
          <MenuItemLeading
            sizeKey={sz}
            isDisabled={isDisabled}
            label={label}
            avatarConfig={avatarConfig}
            highlightIcon={resolvedHighlightIcon}
            icon={resolvedIcon}
          />
          <span className="flex min-w-0 flex-1 flex-col justify-center">
            <span className={joinClasses(MENU_ITEM_LABEL_CLASS[sz], textMuted, 'truncate')}>
              {label}
            </span>
            {subtext ? (
              <span className={joinClasses(MENU_ITEM_DESCRIPTION_CLASS, descMuted, 'truncate')}>
                {subtext}
              </span>
            ) : null}
          </span>
        </span>
      )}

      {showTrailingButton && !isLoading ? (
        <button
          type="button"
          disabled={isDisabled}
          aria-label={closeAriaLabel}
          onClick={handleClose}
          className={joinClasses(
            'inline-flex shrink-0 items-center justify-center text-content',
            'transition-colors hover:bg-black/5 dark:hover:bg-white/10',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent/50',
            'disabled:cursor-not-allowed disabled:text-content-muted',
            MENU_ITEM_CLOSE_PADDING[sz],
          )}
        >
          <X size={MENU_ITEM_CLOSE_ICON_SIZE[sz]} strokeWidth={2} aria-hidden />
        </button>
      ) : null}
    </div>
  );
};

/**
 * Checkbox visual indicator for MenuItem rows.
 * Reflects `checked` and `disabled` without owning any handlers — the parent
 * MenuItem row remains the single source of click/activation.
 */
const CHECKBOX_INDICATOR_SIZE = {
  small: { box: 'h-4 w-4', tick: 10 },
  medium: { box: 'h-5 w-5', tick: 12 },
};

const CheckboxIndicator = ({ size = 'small', checked = false, disabled = false }) => {
  const { box, tick } = CHECKBOX_INDICATOR_SIZE[size] ?? CHECKBOX_INDICATOR_SIZE.small;

  return (
    <span
      aria-hidden
      className={joinClasses(
        'inline-flex shrink-0 items-center justify-center rounded-sm border border-solid transition-colors',
        box,
        disabled
          ? checked
            ? 'border-2 border-[#80AFFF] text-[#80AFFF]'
            : 'border-[#BABABA]'
          : checked
            ? 'border-2 border-[#2173FF] text-[#2173FF] dark:border-blue-400 dark:text-blue-400'
            : 'border-[#5C5C5C] dark:border-gray-400 bg-surface',
      )}
    >
      {checked ? <Check size={tick} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" /> : null}
    </span>
  );
};

export default memo(MenuItem, areMenuItemPropsEqual);
